/**
 * Planned modules — pages the sidebar may link to before they are built.
 *
 * This is the only list of those pages. `plannedStaffNavItems()` feeds the
 * sidebar, `studentPortalNavigation` / `parentPortalNavigation` are built from
 * the same rows, and App.js mounts `plannedRoutePaths()` onto NotAvailableYet.
 * Adding a page here is what makes the sidebar-route test pass.
 */
import {
  Bell,
  Bot,
  Brain,
  Bus,
  Calculator,
  DollarSign,
  GraduationCap,
  Heart,
  LayoutDashboard,
  MessageSquare,
  Shield,
  Stethoscope,
  UserPlus,
  Users,
  Wrench,
} from 'lucide-react';
import { routePatternMatches } from './sidebarRouteCheck';

const page = (name, href, extra = {}) => ({ name, href, ...extra });

export const PLANNED_MODULES = [
  {
    name: 'Payroll',
    module: 'payroll',
    icon: Calculator,
    pages: [
      page('Dashboard', '/payroll'),
      page('My Pay Stubs', '/payroll/my-paystubs'),
      page('Compliance Reports', '/payroll/compliance'),
      page('Employee Contracts', '/payroll/contracts', { permission: 'hiring.hire_onboarding.view' }),
    ],
  },
  {
    name: 'Benefits',
    module: 'benefits',
    icon: Heart,
    pages: [
      page('Dashboard', '/benefits'),
      page('My Enrollment', '/benefits/my-enrollment'),
      page('My Dependents', '/benefits/my-dependents'),
      page('Life Events', '/benefits/life-events'),
      page('Health Benefits', '/benefits/health'),
      page('Health Concierge', '/benefits/health-concierge'),
      page('Retirement Plans', '/benefits/retirement'),
      page('Retirement Concierge', '/benefits/retirement-concierge'),
      page('COBRA Admin', '/benefits/cobra'),
      page('Open Enrollment', '/benefits/open-enrollment'),
      page('ACA Compliance', '/benefits/aca'),
      page('Carrier Feed (EDI 834)', '/benefits/carrier-feed'),
    ],
  },
  {
    name: 'Finance',
    module: 'finance',
    icon: DollarSign,
    pages: [
      page('Dashboard', '/finance'),
      page('Requisitions', '/finance/requisitions'),
      page('Purchase Orders', '/finance/purchase-orders'),
      page('Vendors', '/finance/vendors'),
      page('Invoices', '/finance/invoices'),
      page('Budgets', '/finance/budgets'),
      page('Fund Accounting', '/finance/funds'),
      page('General Ledger', '/finance/general-ledger'),
      page('Reports', '/finance/reports'),
      page('Bidding & Auto-Match', '/finance/bidding'),
    ],
  },
  {
    name: 'SIS',
    module: 'sis',
    icon: GraduationCap,
    pages: [
      page('Dashboard', '/sis'),
      page('Teacher Portal', '/sis/teacher'),
      page('IEP Management', '/sis/iep'),
      page('504 Plans', '/sis/504'),
      page('Master Schedule', '/sis/master-schedule'),
      page('Student Portal', '/sis/student', { portalDashboard: 'student' }),
      page('Parent Portal', '/sis/parent', { portalDashboard: 'parent' }),
      page('Custom Forms', '/sis/custom-forms'),
      page('Health Records', '/sis/health-records'),
      page('Superintendent Portal', '/sis/admin', { superAdminOnly: true }),
      page('My Grades', '/sis/student/grades', { portal: 'student', group: 'My Academics' }),
      page('My Schedule', '/sis/student/schedule', { portal: 'student', group: 'My Academics' }),
      page('My Assignments', '/sis/student/assignments', { portal: 'student', group: 'My Academics' }),
      page('Announcements', '/sis/student/announcements', { portal: 'student', group: 'School Info' }),
      page('Ask AI Tutor', '/sis/student/ai-tutor', { portal: 'student', group: 'AI Tutor' }),
      page('Overview', '/sis/parent/children', { portal: 'parent', group: 'My Children' }),
      page('Grades', '/sis/parent/grades', { portal: 'parent', group: 'My Children' }),
      page('Attendance', '/sis/parent/attendance', { portal: 'parent', group: 'My Children' }),
      page('Performance Analysis', '/sis/parent/ai-insights', { portal: 'parent', group: 'AI Insights' }),
      page('Balance & Add Funds', '/sis/parent/lunch', { portal: 'parent', group: 'Lunch Account' }),
      page('Messages', '/sis/parent/messages', { portal: 'parent', group: 'Communication' }),
    ],
  },
  {
    name: 'Transportation',
    module: 'transportation',
    icon: Bus,
    pages: [
      page('Dashboard', '/transportation'),
      page('Live Telemetry', '/transportation/telemetry'),
      page('Route Optimization', '/transportation/route-optimization'),
      page('Student Ridership', '/transportation/student-ridership'),
      page('Walk Zones', '/transportation/walk-zones'),
      page('Special Needs Transport', '/transportation/special-needs'),
      page('Safety Scores', '/transportation/safety-scores'),
      page('Stop Arm Violations', '/transportation/stop-arm-violations'),
      page('Driver Substitutes', '/transportation/substitutes'),
      page('Communications', '/transportation/communications'),
      page('Predictive Maintenance', '/transportation/predictive-maintenance', { featureKey: 'transport.maintenance' }),
      page('Mechanic Shops', '/transportation/mechanic-shops'),
      page('Parts Inventory', '/transportation/parts-inventory'),
      page('Fuel Analytics', '/transportation/fuel-analytics', { featureKey: 'transport.fuel' }),
      page('Parent Bus Tracker', '/transportation/parent-tracker', { featureKey: 'transport.parent' }),
      page('Integration Hub', '/transportation/integration-hub', { featureKey: 'transport.hub' }),
      page('PTCRS Reimbursement', '/transportation/ptcrs'),
    ],
  },
  {
    name: 'Revenue Programs',
    modules: ['facilities', 'extended_learning', 'childcare', 'career_pathways'],
    icon: DollarSign,
    pages: [
      page('Dashboard', '/revenue'),
      page('Analytics', '/revenue/analytics'),
      page('Facilities Dashboard', '/revenue/facilities'),
      page('Facility Calendar', '/revenue/facilities/calendar'),
      page('Booking Requests', '/revenue/facilities/bookings'),
      page('Events Dashboard', '/revenue/ticketing'),
      page('Create Event', '/revenue/ticketing/create'),
      page('Enrichment Programs', '/revenue/enrichment'),
      page('Childcare Management', '/revenue/childcare'),
      page('Career Pathways', '/revenue/careers'),
    ],
  },
  {
    name: 'Medicaid Reimbursement',
    module: 'medicaid',
    icon: Stethoscope,
    pages: [
      page('Dashboard', '/revenue/medicaid'),
      page('Practitioner Service Log', '/practitioner/service-log'),
      page('Claim Health', '/revenue/medicaid/claim-health'),
      page('Billing Tools', '/revenue/medicaid/billing-tools'),
      page('Compliance', '/revenue/medicaid/compliance'),
      page('IL Onboarding', '/revenue/medicaid/onboarding'),
    ],
  },
  {
    name: 'Substitute Marketplace',
    module: 'substitutes',
    icon: UserPlus,
    pages: [
      page('Dashboard', '/revenue/substitutes'),
      page('SubConnect AI Platform', '/revenue/subconnect'),
      // "Find Substitutes" and "Active Assignments" duplicated the two hrefs above.
      page('Auto Call-Out', '/revenue/substitutes/callouts'),
    ],
  },
  {
    name: 'Compliance',
    module: 'compliance',
    icon: Shield,
    pages: [
      page('Compliance', '/compliance'),
    ],
  },
  {
    name: 'Operations',
    module: 'operations',
    icon: Wrench,
    pages: [
      page('NSLP Meal Claims', '/operations/nslp'),
      page('Facilities Maintenance (CMMS)', '/operations/facilities-cmms'),
    ],
  },
];

export const ONBOARDING_WIZARD = {
  name: 'Onboarding Wizard',
  href: '/district-onboarding',
  module: 'Admin',
  patterns: ['/district-onboarding/:tenantId'],
};

export const DISTRICT_SETTINGS = {
  name: 'District Settings',
  href: '/district-settings',
  module: 'Admin',
};

const PLANNED_ADMIN_PAGES = [ONBOARDING_WIZARD, DISTRICT_SETTINGS];

const PORTAL_GROUP_ICONS = {
  'My Academics': GraduationCap,
  'School Info': Bell,
  'AI Tutor': Bot,
  'My Children': Users,
  'AI Insights': Brain,
  'Lunch Account': DollarSign,
  'Communication': MessageSquare,
};

function staffPages(mod) {
  return mod.pages.filter((item) => !item.portal);
}

export function plannedStaffNavItems() {
  return PLANNED_MODULES.map((mod) => {
    const pages = staffPages(mod);
    const item = {
      name: mod.name,
      href: pages[0].href,
      icon: mod.icon,
      planned: true,
    };
    if (mod.module) item.module = mod.module;
    if (mod.modules) item.modules = mod.modules;
    if (pages.length > 1) {
      item.children = pages.map((entry) => ({
        name: entry.name,
        href: entry.href,
        planned: true,
        ...(entry.featureKey ? { featureKey: entry.featureKey } : {}),
        ...(entry.permission ? { permission: entry.permission } : {}),
        ...(entry.superAdminOnly ? { superAdminOnly: true } : {}),
      }));
    }
    return item;
  });
}

function portalNavigation(portal) {
  const sis = PLANNED_MODULES.find((mod) => mod.module === 'sis');
  const dashboard = sis.pages.find((entry) => entry.portalDashboard === portal);
  const groups = [];
  sis.pages.filter((entry) => entry.portal === portal).forEach((entry) => {
    let group = groups.find((item) => item.name === entry.group);
    if (!group) {
      group = {
        name: entry.group,
        href: entry.href,
        icon: PORTAL_GROUP_ICONS[entry.group],
        planned: true,
        children: [],
      };
      groups.push(group);
    }
    group.children.push({ name: entry.name, href: entry.href, planned: true });
  });
  return [
    {
      name: 'Dashboard',
      href: dashboard.href,
      icon: LayoutDashboard,
      alwaysShow: true,
      planned: true,
    },
    ...groups,
  ];
}

export const studentPortalNavigation = portalNavigation('student');
export const parentPortalNavigation = portalNavigation('parent');

export function withPlannedSection(items) {
  const built = [];
  const planned = [];
  items.forEach((item) => {
    if (item.planned) planned.push(item);
    else built.push(item);
  });
  if (!planned.length) return built;
  return [...built, { name: 'Planned', isSection: true }, ...planned];
}

export function plannedRoutePaths() {
  const paths = [];
  PLANNED_MODULES.forEach((mod) => {
    mod.pages.forEach((entry) => paths.push(entry.href));
  });
  PLANNED_ADMIN_PAGES.forEach((entry) => {
    paths.push(entry.href);
    (entry.patterns || []).forEach((pattern) => paths.push(pattern));
  });
  return [...new Set(paths)];
}

export function findPlannedPage(pathname) {
  const pages = [];
  PLANNED_MODULES.forEach((mod) => {
    mod.pages.forEach((entry) => pages.push({ ...entry, module: mod.name }));
  });
  PLANNED_ADMIN_PAGES.forEach((entry) => pages.push(entry));
  return pages.find((entry) => (
    entry.href === pathname
    || (entry.patterns || []).some((pattern) => routePatternMatches(pattern, pathname))
  )) || null;
}

export function plannedPageLabel(entry) {
  if (!entry) return 'This page';
  if (!entry.module || entry.module === entry.name) return entry.name;
  return `${entry.module} · ${entry.name}`;
}
